/* Coupon and free-gift sheets in the bag.

   The markup comes from snippets/minery-offer-progress.liquid, rendered in the
   cart drawer body and in the cart page footer. Both sheets are native
   <dialog>s, so focus containment and Escape come from the browser, and every
   form posts to a real endpoint, so without this script they still work:
   /cart/update for coupons and /cart/add for the gift, both landing on /cart.

   With the script the shopper stays where they are. Inside the drawer the
   drawer body is re-rendered; on the cart page the page reloads, because
   Dawn's cart.js owns that page's sections and one reload is simpler and
   safer than re-rendering three of them by hand.

   Everything is delegated: the drawer body and the cart footer are replaced
   after every cart change, so per-element listeners would leak or go stale. */

(function () {
  'use strict';

  if (window.mineryCartOffers) return;
  window.mineryCartOffers = true;

  var routes = window.routes || {};
  var UPDATE_URL = (routes.cart_update_url || '/cart/update') + '.js';
  var ADD_URL = (routes.cart_add_url || '/cart/add') + '.js';
  var CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  var CART_URL = (routes.cart_url || '/cart') + '.js';

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  function getCart() {
    return fetch(CART_URL, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* ------------------------------------------------------------------
     Open / close
     ------------------------------------------------------------------ */

  /* Wide screens: the sheet opens where it was asked for, not in the middle
     of the page. Over the summary card on the cart page (same top, left and
     width, growing from the row that was tapped), or rising inside the drawer
     panel. Phones keep the plain bottom sheet. */
  function anchor(sheet, opener) {
    sheet.classList.remove('is-over-card', 'is-in-drawer');
    sheet.removeAttribute('style');
    if (!opener || !window.matchMedia('(min-width: 750px)').matches) return;

    var drawer = opener.closest('.m-cdraw__dialog');
    var card = opener.closest('#main-cart-footer .cart__blocks');

    if (drawer) {
      // The panel's resting place, from the stylesheet; its live rect can
      // still be mid-slide.
      sheet.classList.add('is-in-drawer');
    } else if (card && window.matchMedia('(min-width: 1200px)').matches) {
      var c = card.getBoundingClientRect();
      var o = opener.getBoundingClientRect();
      var top = Math.max(16, c.top);
      sheet.classList.add('is-over-card');
      sheet.style.setProperty('--sheet-top', top + 'px');
      sheet.style.setProperty('--sheet-left', c.left + 'px');
      sheet.style.setProperty('--sheet-width', c.width + 'px');
      sheet.style.setProperty('--sheet-origin', (o.top + o.height / 2 - top) + 'px');
    }
  }

  function openSheet(id, opener) {
    var sheet = document.getElementById(id);
    if (!sheet || sheet.open) return;
    sheet._opener = opener || null;
    clearError(sheet);
    anchor(sheet, opener);
    sheet.showModal();
  }

  function closeSheet(sheet) {
    if (!sheet || !sheet.open) return;
    sheet.close();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-m-sheet-open]');
    if (opener) {
      e.preventDefault();
      openSheet(opener.getAttribute('data-m-sheet-open'), opener);
      return;
    }

    if (e.target.closest('[data-m-sheet-close]')) {
      e.preventDefault();
      closeSheet(e.target.closest('dialog'));
      return;
    }

    // A click on the backdrop reports the dialog itself as the target.
    if (e.target.matches && e.target.matches('dialog.m-sheet')) {
      closeSheet(e.target);
    }
  });

  // `close` does not bubble, so it is caught on the way down. Focus goes back
  // to whatever opened the sheet.
  document.addEventListener('close', function (e) {
    var sheet = e.target;
    if (!sheet.matches || !sheet.matches('dialog.m-sheet')) return;
    var opener = sheet._opener;
    if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
  }, true);

  /* ------------------------------------------------------------------
     After a change: re-render where the shopper is
     ------------------------------------------------------------------ */

  function afterChange(origin) {
    var drawer = origin.closest('minery-cart-drawer');
    var sheet = origin.closest('dialog.m-sheet');

    if (drawer && typeof drawer.refresh === 'function') {
      if (sheet) closeSheet(sheet);
      return Promise.all([
        drawer.refresh(),
        fetch('/?sections=cart-icon-bubble')
          .then(function (r) { return r.json(); })
          .then(function (s) {
            if (typeof drawer.updateBubble === 'function') drawer.updateBubble(s['cart-icon-bubble']);
          })
          .catch(function () {})
      ]);
    }

    window.location.reload();
    return Promise.resolve();
  }

  function busy(form, on) {
    var button = form.querySelector('button[type="submit"]');
    if (!button) return;
    button.disabled = on;
    button.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------
     Coupons
     ------------------------------------------------------------------ */

  function showError(form, text) {
    var sheet = form.closest('dialog.m-sheet');
    var slot = sheet && sheet.querySelector('[data-m-coupon-error]');
    if (slot) slot.textContent = text;
  }

  function clearError(sheet) {
    var slot = sheet.querySelector('[data-m-coupon-error]');
    if (slot) slot.textContent = '';
  }

  function applyCoupon(form) {
    var field = form.querySelector('[name="discount"]');
    var code = field ? field.value.trim().toUpperCase() : '';
    var typed = form.hasAttribute('data-m-coupon-typed');

    if (typed && !code) {
      showError(form, 'Enter a coupon code.');
      field.focus();
      return;
    }

    busy(form, true);
    if (typed) showError(form, '');

    var before = [];

    getCart()
      .then(function (cart) {
        before = (cart.discount_codes || [])
          .filter(function (d) { return d.applicable; })
          .map(function (d) { return d.code; });
        return post(UPDATE_URL, { discount: code });
      })
      .then(function () {
        if (!code) return null;
        return getCart();
      })
      .then(function (cart) {
        if (cart) {
          var ok = (cart.discount_codes || []).some(function (d) {
            return d.applicable && d.code.toUpperCase() === code;
          });
          if (!ok) {
            // Shopify keeps a code it cannot use on the cart, marked not
            // applicable. Put back whatever was there before, and say why.
            return post(UPDATE_URL, { discount: before.join(',') }).then(function () {
              throw { message: code + " can't be used on this bag." };
            });
          }
        }
        return afterChange(form);
      })
      .catch(function (err) {
        busy(form, false);
        showError(form, (err && err.message) || "That didn't work. Try again.");
      });
  }

  /* ------------------------------------------------------------------
     Free gift
     ------------------------------------------------------------------ */

  function addGift(form) {
    var picked = form.querySelector('input[name="id"]:checked');
    if (!picked) return;

    var replace = form.getAttribute('data-replace');
    busy(form, true);

    var first = replace
      ? post(CHANGE_URL, { id: replace, quantity: 0 })
      : Promise.resolve();

    first
      .then(function () {
        return post(ADD_URL, { items: [{ id: Number(picked.value), quantity: 1 }] });
      })
      .then(function () {
        return afterChange(form);
      })
      .catch(function () {
        busy(form, false);
        // Fall back to the plain form post, which lands on the cart page.
        form.submit();
      });
  }

  document.addEventListener('change', function (e) {
    var radio = e.target;
    if (!radio.matches('form[data-m-gift-form] input[name="id"]')) return;
    var form = radio.form;

    var label = form.querySelector('[data-m-gift-picked]');
    if (label) label.textContent = radio.getAttribute('data-name') || '';

    var img = form.querySelector('[data-m-gift-media] img');
    var src = radio.getAttribute('data-img');
    if (img && src) {
      img.removeAttribute('srcset');
      img.src = src;
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form.matches('form[data-m-coupon-form]')) {
      e.preventDefault();
      applyCoupon(form);
    } else if (form.matches('form[data-m-gift-form]')) {
      e.preventDefault();
      addGift(form);
    }
  });
})();
